import { api } from '@/lib/api';
import { build_params, download_export } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type {
  NearbyWalkersResponse,
  SupportCounts,
  SupportRequestDetail,
  SupportRequestListFilters,
  SupportRequestListResponse,
} from './types';

/**
 * The queue, with whatever filters and sort are set.
 *
 * Through `build_params` rather than a hand-written if-chain: the old version
 * enumerated eight filters by hand, so any filter added to the type had to be
 * remembered here too, and one that was not would look alive while sending
 * nothing.
 */
export async function fetch_support_request_list(
  filters: SupportRequestListFilters,
  sort?: SortState,
): Promise<SupportRequestListResponse> {
  const res = await api.get<SupportRequestListResponse>('/admin/support-requests', {
    params: build_params(filters as Record<string, never>, sort),
  });
  return res.data;
}

export async function fetch_support_counts(): Promise<SupportCounts> {
  const res = await api.get<SupportCounts>('/admin/support-requests/counts');
  return res.data;
}

export async function export_support_requests(
  filters: SupportRequestListFilters,
  sort?: SortState,
): Promise<void> {
  await download_export(
    '/admin/support-requests/export',
    build_params(filters as Record<string, never>, sort),
    'support-requests.csv',
  );
}

export async function fetch_support_request_detail(id: string): Promise<SupportRequestDetail> {
  const res = await api.get<SupportRequestDetail>(`/admin/support-requests/${id}`);
  return res.data;
}

export async function fetch_nearby_walkers(id: string): Promise<NearbyWalkersResponse> {
  const res = await api.get<NearbyWalkersResponse>(`/admin/support-requests/${id}/nearby-walkers`);
  return res.data;
}

export async function reassign_walk(
  id: string,
  input: { vendor_profile_id: string; payout_amount_inr: number },
): Promise<SupportRequestDetail> {
  const res = await api.post<SupportRequestDetail>(`/admin/support-requests/${id}/reassign`, input);
  return res.data;
}

export async function propose_slot(id: string, slot_start: string): Promise<SupportRequestDetail> {
  const res = await api.post<SupportRequestDetail>(`/admin/support-requests/${id}/propose`, {
    slot_start,
  });
  return res.data;
}

export async function refund_session(
  id: string,
  amount_inr?: number,
): Promise<SupportRequestDetail> {
  const res = await api.post<SupportRequestDetail>(
    `/admin/support-requests/${id}/refund`,
    amount_inr != null ? { amount_inr } : {},
  );
  return res.data;
}
